import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Database, 
  Server, 
  CheckCircle, 
  ShieldCheck, 
  TrendingUp, 
  RefreshCw, 
  AlertTriangle, 
  Coins, 
  Sparkles, 
  Terminal, 
  Activity, 
  Cpu, 
  Layers, 
  Network, 
  ArrowRightLeft, 
  Heart,
  Scale
} from 'lucide-react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { EmpireFinancials } from '../lib/NexoraxFinancialRecovery';
import { TruthRegistry, VerifiedTransaction } from '../lib/NexoraxTruthRegistry';
import { GlobalRegistry, ClientDealRecord } from '../lib/NexoraxGlobalRegistry';

export function NexoraXCommandLedgerConsole() {
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'IDLE' | 'PROCESSING' | 'SYNCHRONIZED'>('IDLE');
  
  // Real-time fetched state buffers from across the nodes
  const [bankingCapital, setBankingCapital] = useState({
    primarySecurePaymentSettlement: 0,
    NexoraXInvestmentCap: 0,
    timestamp: ''
  });

  const [invoices, setInvoices] = useState<VerifiedTransaction[]>([]);
  const [firebaseClients, setFirebaseClients] = useState<any[]>([]);
  const [dealRegistryLedger, setDealRegistryLedger] = useState<ClientDealRecord[]>([]);

  // Simulation controls state
  const [simulatedGrossInput, setSimulatedGrossInput] = useState('');
  const [simulationResult, setSimulationResult] = useState<{
    gross: number;
    levy: number;
    charity1: number;
    charity2: number;
    charity3: number;
    tax: number;
    net: number;
  } | null>(null);

  // Load and subscribe to real-time data sources
  useEffect(() => {
    // 1. Fetch Node-01 Bank Liquidity (Self-restored data)
    const cap = EmpireFinancials.getCoreCapitalSnapshot();
    setBankingCapital({
      primarySecurePaymentSettlement: cap.primarySecurePaymentSettlement,
      NexoraXInvestmentCap: cap.NexoraXInvestmentCap,
      timestamp: new Date().toISOString()
    });

    // 2. Load Local NexoraX Verified Invoices from Truth Registry
    setInvoices(TruthRegistry.getVerifiedTransactions());

    // 3. Load Deal Registry (Static or localStorage ledger entries)
    setDealRegistryLedger(GlobalRegistry.ledger);

    // 4. Load Live Staged Client Deals via Firebase collection
    const q = query(collection(db, 'clients'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: any[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        const valueNum = parseFloat(String(data.value || '').replace(/[^0-9.]/g, '')) || 0;
        list.push({
          id: doc.id,
          name: data.name || 'Ecosystem Partner',
          company: data.company || 'Enterprise Entity',
          value: valueNum,
          status: data.status || 'STAGED',
          timestamp: data.lastContact || new Date().toISOString()
        });
      });
      setFirebaseClients(list);
    }, (err) => {
      console.error("NexoraXCommandLedger: Firebase Live Client subscription issue", err);
    });

    return () => unsubscribe();
  }, []);

  // Recalculates master aggregate outputs
  const handleSystemReactivationSync = () => {
    setLoading(true);
    setSyncStatus('PROCESSING');
    
    setTimeout(() => {
      // Re-read current snapshots
      const freshCap = EmpireFinancials.getCoreCapitalSnapshot();
      setBankingCapital({
        primarySecurePaymentSettlement: freshCap.primarySecurePaymentSettlement,
        NexoraXInvestmentCap: freshCap.NexoraXInvestmentCap,
        timestamp: new Date().toISOString()
      });
      setInvoices(TruthRegistry.getVerifiedTransactions());
      setDealRegistryLedger(GlobalRegistry.ledger);
      
      setSyncStatus('SYNCHRONIZED');
      setLoading(false);
      
      setTimeout(() => {
        setSyncStatus('IDLE');
      }, 3000);
    }, 1000);
  };

  // Run instant standard multi-stage tax accounting on custom inputs
  const runExtractionSimulation = (e: React.FormEvent) => {
    e.preventDefault();
    const val = parseFloat(simulatedGrossInput);
    if (isNaN(val) || val <= 0) return;

    const levy = val * 0.01;
    const remainingAfterLevy = val - levy;
    const tax = remainingAfterLevy * 0.20;
    const net = remainingAfterLevy - tax;

    // Direct 33.33% split calculation
    const charityUnit = levy / 3.0;

    setSimulationResult({
      gross: val,
      levy,
      charity1: charityUnit,
      charity2: charityUnit,
      charity3: charityUnit,
      tax,
      net
    });
  };

  // Aggregate Calculations
  const invoiceGrossTotal = invoices.reduce((acc, curr) => acc + curr.amount, 0);
  const firebasePotentialValueSum = firebaseClients.reduce((acc, curr) => acc + curr.value, 0);
  const localDealsPotentialValueSum = dealRegistryLedger.reduce((acc, curr) => acc + curr.amount, 0);

  // Multi-Stage Aggregated Financial Summary
  const aggregateRevenuePool = invoiceGrossTotal + firebasePotentialValueSum;
  const aggregateNexoraXLevy = aggregateRevenuePool * 0.01;
  const aggregateTaxExtract = (aggregateRevenuePool - aggregateNexoraXLevy) * 0.20;
  const aggregateNetfinance = aggregateRevenuePool - aggregateNexoraXLevy - aggregateTaxExtract;

  // Split calculations
  const charity1Dunfermline = aggregateNexoraXLevy * 0.333333;
  const charity2Global = aggregateNexoraXLevy * 0.333333;
  const charity3UK = aggregateNexoraXLevy * 0.333334;

  return (
    <div className="flex flex-col space-y-8 w-full max-w-6xl mx-auto p-4 md:p-10 font-mono text-slate-300">
      
      {/* Visual Header Grid Panel */}
      <div className="relative border border-slate-800 bg-[#030612]/95 rounded-2xl p-6 sm:p-8 overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 blur-[100px] rounded-full pointer-events-none"></div>
        
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 pb-6 border-b border-slate-900">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-emerald-950/60 border border-emerald-500/30 text-emerald-400 text-[9px] font-extrabold uppercase px-2.5 py-1 rounded">
                PROTOCOL: NODE-21
              </span>
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-[9.5px] text-emerald-400 font-extrabold tracking-widest uppercase">
                COMMAND LEDGER AGGREGATOR
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-sans font-black text-white uppercase tracking-tight">
              NEXORAX COMMAND CONSOLE
            </h1>

            {/* Theological Preamble Integration */}
            <p className="text-xs font-bold text-slate-400 italic font-serif mt-2 border-l-2 border-slate-800 pl-3">
              "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleSystemReactivationSync}
              disabled={loading}
              className={`px-5 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest border transition-all cursor-pointer flex items-center gap-2 ${
                syncStatus === 'SYNCHRONIZED'
                  ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-400'
                  : 'bg-[#050914] border-slate-700 text-white hover:border-emerald-500 hover:text-emerald-400'
              }`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {syncStatus === 'PROCESSING' 
                ? 'SYNCING SOURCES...' 
                : syncStatus === 'SYNCHRONIZED' 
                ? 'AGGREGATION VERIFIED' 
                : 'RUN UNIFIED TELEMETRY SYNC'}
            </button>
          </div>
        </div>

        {/* Real-time System Connectivity Metrics Grid (1, 2, 3...) Header */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6">
          <div className="bg-[#01030b] border border-slate-900 rounded-xl p-4">
            <span className="text-[9px] text-slate-500 font-extrabold uppercase tracking-widest block mb-1">
              1. COMMAND STATUS
            </span>
            <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5 font-mono">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce"></span>
              NODE-21 LIVE
            </span>
          </div>

          <div className="bg-[#01030b] border border-slate-900 rounded-xl p-4">
            <span className="text-[9px] text-slate-500 font-extrabold uppercase tracking-widest block mb-1">
              2. SOURCES ENGAGED
            </span>
            <span className="text-xs font-extrabold text-[#00ffff] font-mono">
              NODES 01, 11, 12
            </span>
          </div>

          <div className="bg-[#01030b] border border-slate-900 rounded-xl p-4">
            <span className="text-[9px] text-slate-500 font-extrabold uppercase tracking-widest block mb-1">
              3. DEPLOYMENT REGISTRY
            </span>
            <span className="text-xs font-bold text-fuchsia-400 font-mono">
              SECURE SYNC DIRECT
            </span>
          </div>

          <div className="bg-[#01030b] border border-slate-900 rounded-xl p-4">
            <span className="text-[9px] text-slate-500 font-extrabold uppercase tracking-widest block mb-1">
              4. LATENCY INDEX
            </span>
            <span className="text-xs font-bold text-slate-300 font-mono">
              0.8s METRIC STABLE
            </span>
          </div>
        </div>
      </div>

      {/* CORE AGGREGATION MATRIX */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* SOURCE 1: NODE-01 LIQUIDITY */}
        <div className="bg-[#030612] border border-slate-800 rounded-2xl p-5 md:p-6 flex flex-col justify-between shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 blur-[50px] rounded-full pointer-events-none"></div>
          
          <div>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-cyan-400" />
                <h3 className="text-xs font-extrabold text-cyan-400 uppercase tracking-widest">
                  NODE-01: BANK LIQUIDITY
                </h3>
              </div>
              <span className="text-[8px] bg-cyan-950/40 border border-cyan-500/20 px-1.5 py-0.5 rounded text-cyan-300 font-bold">
                SecurePayment SECURE
              </span>
            </div>

            <p className="text-[10px] text-slate-400 uppercase tracking-wide leading-relaxed mb-6">
              Captures live corporate liquidity reserves and multiversal growth resource buffers directly from Node-01 Bank configuration snapshots.
            </p>

            <div className="space-y-4">
              <div className="bg-black/40 border border-slate-900 p-4 rounded-xl">
                <span className="text-[8px] text-slate-500 font-extrabold uppercase block mb-1">
                  PRIMARY SecurePayment CASH RESERVES
                </span>
                <span className="text-2xl font-black text-white">
                  £{bankingCapital.primarySecurePaymentSettlement.toLocaleString()}
                </span>
              </div>

              <div className="bg-black/40 border border-slate-900 p-4 rounded-xl">
                <span className="text-[8px] text-slate-500 font-extrabold uppercase block mb-1">
                  NEXORAX resource BUDGET
                </span>
                <span className="text-2xl font-black text-white">
                  £{bankingCapital.NexoraXInvestmentCap.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-900 flex justify-between text-[8px] text-slate-500 font-bold">
            <span>MAPPED SNAPSHOT REFERENCE</span>
            <span className="text-cyan-400">ACTIVE HANDSHAKE</span>
          </div>
        </div>

        {/* SOURCE 2: NODE-11 INVOICE TELEMETRY */}
        <div className="bg-[#030612] border border-slate-800 rounded-2xl p-5 md:p-6 flex flex-col justify-between shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[50px] rounded-full pointer-events-none"></div>

          <div>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-emerald-400" />
                <h3 className="text-xs font-extrabold text-emerald-400 uppercase tracking-widest">
                  NODE-11: VERIFIED HARVEST
                </h3>
              </div>
              <span className="text-[8px] bg-emerald-950/40 border border-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-300 font-bold">
                TRUTH-ONLY
              </span>
            </div>

            <p className="text-[10px] text-slate-400 uppercase tracking-wide leading-relaxed mb-6">
              Aggregates verified invoices logs from Stripe, PayPal, Transfer, and Firebase synchronized client accounts.
            </p>

            <div className="space-y-4">
              <div className="bg-black/40 border border-slate-900 p-4 rounded-xl">
                <span className="text-[8px] text-slate-500 font-extrabold uppercase block mb-1">
                  AGGREGATED GROSS REVENUE
                </span>
                <span className="text-2xl font-black text-white">
                  £{aggregateRevenuePool.toLocaleString()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#010408] border border-slate-950 p-2.5 rounded-lg">
                  <span className="text-[7.5px] text-slate-500 font-bold uppercase block">
                    TOTAL INVOICES
                  </span>
                  <span className="text-xs font-bold text-slate-300">
                    {invoices.length} Verified
                  </span>
                </div>
                <div className="bg-[#010408] border border-slate-950 p-2.5 rounded-lg">
                  <span className="text-[7.5px] text-slate-500 font-bold uppercase block">
                    Secure Payment Provider PORT
                  </span>
                  <span className="text-xs font-bold text-emerald-400">
                    Sort: Verified Payment Configuration
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-900 flex justify-between text-[8px] text-slate-500 font-bold">
            <span>PERMANENT TAX LEDGERS</span>
            <span className="text-emerald-400">ROUTING VERIFIED</span>
          </div>
        </div>

        {/* SOURCE 3: NODE-12 DEAL STAGING */}
        <div className="bg-[#030612] border border-slate-800 rounded-2xl p-5 md:p-6 flex flex-col justify-between shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-500/5 blur-[50px] rounded-full pointer-events-none"></div>

          <div>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-fuchsia-400" />
                <h3 className="text-xs font-extrabold text-fuchsia-400 uppercase tracking-widest">
                  NODE-12: DEAL REGISTRY
                </h3>
              </div>
              <span className="text-[8px] bg-fuchsia-950/40 border border-fuchsia-500/20 px-1.5 py-0.5 rounded text-fuchsia-300 font-bold">
                STAGED DEALS
              </span>
            </div>

            <p className="text-[10px] text-slate-400 uppercase tracking-wide leading-relaxed mb-6">
              Monitors pending potential pipeline deals from the global corporate registries and Firebase active data collectors.
            </p>

            <div className="space-y-4">
              <div className="bg-black/40 border border-slate-900 p-4 rounded-xl">
                <span className="text-[8px] text-slate-500 font-extrabold uppercase block mb-1">
                  POTENTIAL PIPELINE CAPITAL
                </span>
                <span className="text-2xl font-black text-white">
                  £{(firebasePotentialValueSum + localDealsPotentialValueSum).toLocaleString()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#010408] border border-slate-950 p-2.5 rounded-lg">
                  <span className="text-[7.5px] text-slate-500 font-bold uppercase block">
                    FIREBASE STAGES
                  </span>
                  <span className="text-xs font-bold text-slate-300">
                    {firebaseClients.length} Staged
                  </span>
                </div>
                <div className="bg-[#010408] border border-slate-950 p-2.5 rounded-lg">
                  <span className="text-[7.5px] text-slate-500 font-bold uppercase block">
                    LOCAL REGISTRY
                  </span>
                  <span className="text-xs font-bold text-fuchsia-400">
                    {dealRegistryLedger.length} Records
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-900 flex justify-between text-[8px] text-slate-500 font-bold">
            <span>IMPERIAL SECURITY CHECK</span>
            <span className="text-fuchsia-400">NORMAL SYNC</span>
          </div>
        </div>

      </div>

      {/* UNIFIED LAWS & NEXORAX EXTRACTION LEDGER VIEW */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Dynamic Aggregation Telemetry Matrix & Fund Distributions */}
        <div className="lg:col-span-8 bg-[#030612] border border-slate-850 rounded-2xl p-6 shadow-xl relative">
          <div className="flex items-center gap-2 pb-4 border-b border-slate-900 mb-6">
            <Activity className="w-5 h-5 text-emerald-400 animate-pulse" />
            <h2 className="text-sm font-extrabold text-[#00ffff] uppercase tracking-widest">
              5. UNIFIED LEDGER AGGREGATION & NEXORAX ACCOUNTING ENGINE
            </h2>
          </div>

          {/* Aggregation Data Board */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#02040a] border border-cyan-950 rounded-xl p-4 text-left">
              <span className="text-[8px] text-slate-500 font-black block tracking-widest uppercase mb-1">
                COMBINED HARVEST POOL (01 + 11)
              </span>
              <span className="text-xl font-black text-white block">
                £{(bankingCapital.primarySecurePaymentSettlement + aggregateRevenuePool).toLocaleString()}
              </span>
              <span className="text-[7.5px] text-slate-500 font-mono block mt-2">
                RECONCILED NEXORAX CAPITAL SNAPSHOT
              </span>
            </div>

            <div className="bg-[#02040a] border border-emerald-950 rounded-xl p-4 text-left">
              <span className="text-[8px] text-slate-500 font-black block tracking-widest uppercase mb-1">
                3% Community Allocation CAPTURED (1%)
              </span>
              <span className="text-xl font-black text-emerald-400 block">
                £{aggregateNexoraXLevy.toLocaleString()}
              </span>
              <span className="text-[7.5px] text-[#10b981] font-mono block mt-2 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                PERPETUAL LEVY ACTIVE
              </span>
            </div>

            <div className="bg-[#02040a] border border-indigo-950 rounded-xl p-4 text-left">
              <span className="text-[8px] text-slate-500 font-black block tracking-widest uppercase mb-1">
                10% Maintenance & Hosting Fee EXTRACT (20%)
              </span>
              <span className="text-xl font-black text-indigo-400 block">
                £{aggregateTaxExtract.toLocaleString()}
              </span>
              <span className="text-[7.5px] text-indigo-400/80 font-mono block mt-2">
                20% PERFORMANCE ROYALTY SECURED
              </span>
            </div>
          </div>

          {/* Tax Allocations & Charity Distributions Breakdown */}
          <div className="bg-black/45 border border-slate-900 rounded-xl p-5 mb-6">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <Scale className="w-3.5 h-3.5 text-slate-400" />
              6. MULTI-STAGE NEXORAX TAX ROYALTY ALLOCATIONS (SecurePayment SORT CODE: Verified Payment Configuration)
            </h3>

            <div className="space-y-3 font-mono">
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-950">
                <span className="text-slate-500">AGGREGATE GROSS REVENUE:</span>
                <span className="text-white font-extrabold">£{aggregateRevenuePool.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-950">
                <span className="text-slate-500">3% Community Allocation FOR FOUNDATIONS (1% PERPETUAL):</span>
                <span className="text-red-400/80 font-extrabold">-£{aggregateNexoraXLevy.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-950 pl-4 bg-red-950/10">
                <span className="text-[10px] text-slate-500">• CHARITY 1 DUNFERMLINE (33.33%Split):</span>
                <span className="text-red-400/80">£{charity1Dunfermline.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-950 pl-4 bg-red-950/10">
                <span className="text-[10px] text-slate-500">• CHARITY 2 GLOBAL FOUNDATION (33.33%Split):</span>
                <span className="text-red-400/80">£{charity2Global.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-950 pl-4 bg-red-950/10">
                <span className="text-[10px] text-slate-500">• CHARITY 3 UNITED KINGDOM (33.33%Split):</span>
                <span className="text-red-400/80">£{charity3UK.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-950">
                <span className="text-slate-500">10% Maintenance & Hosting Fee ROYALTY (20% PERFORMANCE ROYALTY):</span>
                <span className="text-indigo-400/80 font-extrabold">-£{aggregateTaxExtract.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-sm py-2 bg-emerald-950/10 rounded-lg px-3 mt-4 border border-emerald-500/20">
                <span className="text-[#10b981] font-extrabold">7. NET SETTLEMENT TO Secure Payment Provider:</span>
                <span className="text-[#10b981] font-black text-lg">
                  £{aggregateNetfinance.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Active Merged Ledger Registry */}
          <div className="space-y-3">
            <h3 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
              <Network className="w-3.5 h-3.5 text-slate-400" />
              8. VERIFIED ACTIVE STREAMING SETTLEMENT LOG (COMBINED TELEMETRY)
            </h3>
            
            <div className="max-h-52 overflow-y-auto space-y-2 pr-1 scrollbar-thin">
              {invoices.length === 0 && firebaseClients.length === 0 ? (
                <div className="text-slate-500 text-xs text-center py-6">
                  NO ACTIVE TRANSACTION STREAMS DETECTED.
                </div>
              ) : (
                <>
                  {/* Local verified logs */}
                  {invoices.map((tx) => (
                    <div key={tx.id} className="bg-black/35 border border-slate-900 rounded-lg p-3 flex justify-between items-center text-[10px]">
                      <div className="flex flex-col text-left">
                        <span className="text-white font-extrabold uppercase">{tx.clientName}</span>
                        <span className="text-slate-500 mt-0.5 font-mono">{new Date(tx.timestamp).toLocaleString()} ({tx.channel})</span>
                      </div>
                      <div className="text-right">
                        <span className="text-emerald-400 font-extrabold block">£{tx.amount.toLocaleString()}</span>
                        <span className="text-[8px] bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 px-1 py-0.5 rounded uppercase font-bold tracking-widest mt-0.5 inline-block">
                          {tx.status}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Firebase staged deal records */}
                  {firebaseClients.map((client) => (
                    <div key={client.id} className="bg-gradient-to-r from-fuchsia-950/10 to-transparent border border-slate-900 rounded-lg p-3 flex justify-between items-center text-[10px]">
                      <div className="flex flex-col text-left">
                        <span className="text-fuchsia-400 font-extrabold uppercase">{client.name}</span>
                        <span className="text-slate-500 mt-0.5 font-mono">FIREBASE DATABASE // {client.company}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-extrabold block">£{client.value.toLocaleString()}</span>
                        <span className="text-[8px] bg-[#2e0854]/40 border border-[#7c3aed]/40 text-purple-300 px-1 py-0.5 rounded uppercase font-bold tracking-widest mt-0.5 inline-block">
                          {client.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Accounting Tool Simulation Panel */}
        <div className="lg:col-span-4 flex flex-col justify-between space-y-6">
          
          {/* Node Extraction Auditor Tool */}
          <div className="bg-[#030612] border border-slate-800 rounded-2xl p-5 md:p-6 shadow-xl relative overflow-hidden flex-1 flex flex-col justify-between">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-[50px] rounded-full pointer-events-none"></div>

            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-slate-900 mb-4">
                <Terminal className="w-4 h-4 text-indigo-400" />
                <h3 className="text-xs font-black text-indigo-400 uppercase tracking-widest">
                  9. HARVEST TAX COGNITIVE ENGINE
                </h3>
              </div>
              
              <p className="text-[10px] text-slate-400 uppercase tracking-wide leading-relaxed mb-4">
                Enter simulated gross revenue values below to trace multi-stage allocations on secondary business targets instantly.
              </p>

              <form onSubmit={runExtractionSimulation} className="space-y-4">
                <div>
                  <label className="text-[8px] text-slate-500 font-black uppercase tracking-widest block mb-1">
                    ENTER SIMULATED REVENUE AMOUNT (GBP)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={simulatedGrossInput}
                      onChange={(e) => setSimulatedGrossInput(e.target.value)}
                      placeholder="e.g. 250000"
                      className="bg-[#01030b] border border-slate-800 focus:border-indigo-500 focus:outline-none rounded-lg px-3 py-2 text-xs text-white placeholder-slate-600 font-mono flex-1 leading-none"
                    />
                    <button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-500 text-white font-black text-[9px] uppercase tracking-widest px-4 py-2 rounded-lg cursor-pointer transition-colors leading-none"
                    >
                      CALCULATE
                    </button>
                  </div>
                </div>
              </form>

              {/* Simulation Result Displays */}
              <AnimatePresence mode="wait">
                {simulationResult && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-black/60 border border-indigo-550/25 rounded-xl p-4 mt-4 space-y-2.5 text-xs text-left"
                  >
                    <div className="flex justify-between font-bold border-b border-slate-900 pb-1.5 text-[10px]">
                      <span className="text-indigo-400">10. CALCULATED ALLOCATIONS:</span>
                      <span className="text-slate-400">£{simulationResult.gross.toLocaleString()}</span>
                    </div>

                    <div className="flex justify-between text-[10px]">
                      <span className="text-slate-500">1% Perpetual Levy:</span>
                      <span className="text-slate-300 font-bold">£{simulationResult.levy.toLocaleString()}</span>
                    </div>

                    <div className="grid grid-cols-3 gap-1 pl-3 border-l border-slate-800 text-[8.5px] text-slate-400">
                      <div>
                        <span className="block font-black text-slate-500">CHARITY 1</span>
                        <span>£{simulationResult.charity1.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </div>
                      <div>
                        <span className="block font-black text-slate-500">CHARITY 2</span>
                        <span>£{simulationResult.charity2.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </div>
                      <div>
                        <span className="block font-black text-slate-500">CHARITY 3</span>
                        <span>£{simulationResult.charity3.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>

                    <div className="flex justify-between text-[10px]">
                      <span className="text-slate-500">20% Performance Tax:</span>
                      <span className="text-slate-300 font-bold">£{simulationResult.tax.toLocaleString()}</span>
                    </div>

                    <div className="flex justify-between text-[11px] font-black pt-2 border-t border-slate-900 bg-emerald-950/15 rounded px-2">
                       <span className="text-emerald-400 uppercase">SecurePayment Safe NET:</span>
                       <span className="text-emerald-400">£{simulationResult.net.toLocaleString()}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-900 text-[8px] text-slate-600 font-bold">
              ENGINES RUNNING IN STRICT REALTIME MODE
            </div>
          </div>

          {/* Quick Routing Action Desk */}
          <div className="bg-[#030612] border border-slate-800 rounded-2xl p-5 md:p-6 shadow-xl relative overflow-hidden text-left">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-900 pb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              11. INSTANT DEBRIS RECOVERY SYNC PROTOCOLS
            </h3>

            <div className="space-y-3">
              <div className="p-3 bg-[#010309] border border-slate-900 rounded-lg flex items-start gap-2.5">
                <Network className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-[10px]">
                  <span className="font-extrabold text-white block">Secure Payment Provider VERIFIED CORE</span>
                  <span className="text-slate-500">All direct settlements route completely to Sort Code Verified Payment Configuration, A/C: Secure Billing Provider. Mirroring logs saved safely to NexoraX Resource Library.</span>
                </div>
              </div>

              <div className="p-3 bg-[#010309] border border-slate-900 rounded-lg flex items-start gap-2.5">
                <Cpu className="w-4 h-4 text-fuchsia-400 shrink-0 mt-0.5" />
                <div className="text-[10px]">
                  <span className="font-extrabold text-white block">COGNITIVE DEFENSE INCLUSION</span>
                  <span className="text-slate-500">Actively tracks brand variants routing across regional server switches in India, safeguarding Nexorax brand integrity.</span>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
